import React, { useEffect, useCallback, useMemo, useState } from "react";
import "./viewPropertyResale.css";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
    viewPropertyResale,
    statusPropertyResale,
} from "../../Redux/Actions/propertiesResaleActions";
import MainLayout from "../../Layout/mainLayout";
import AdminMarginPopup from "./addAdminMarginPopup";

/* ─── Helpers ─── */
const currencySymbolMap = { INR: "₹", USD: "$", AED: "د.إ", EUR: "€" };

const formatCurrency = (val, currency = "INR") => {
    if (!val && val !== 0) return "—";
    const symbol = currencySymbolMap[currency] || currencySymbolMap.INR;
    if (val >= 10000000) return `${symbol}${(val / 10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `${symbol}${(val / 100000).toFixed(2)} Lacs`;
    return `${symbol}${Number(val).toLocaleString("en-IN")}`;
};

const BoolTag = ({ value }) => (
    <span className={`vrp-bool-tag vrp-bool-tag--${value ? "yes" : "no"}`}>
        {value ? "✓ Yes" : "✗ No"}
    </span>
);

const InfoItem = ({ label, value, full }) => (
    <div className={`vrp-info-item${full ? " vrp-info-item--full" : ""}`}>
        <span className="vrp-info-label">{label}</span>
        <span className="vrp-info-value">{value ?? "—"}</span>
    </div>
);

const CardSection = ({ badge, title, children }) => (
    <div className="vrp-card">
        <div className="vrp-card-header">
            <span className="vrp-card-badge">{badge}</span>
            <h2 className="vrp-card-title">{title}</h2>
        </div>
        {children}
    </div>
);

/* ─── Main Component ─── */
const ViewPropertyResale = () => {
    const { id } = useParams();
    const dispatch = useDispatch();

    const [adminMarginPopup, setAdminMarginPopup] = useState(false);

    const { admin } = useSelector((state) => state.admin || {});
    const accessCode = admin?.data?.access_code || [];
    const canAdd = Array.isArray(accessCode) && accessCode.includes("add");
    const canEdit = Array.isArray(accessCode) && accessCode.includes("edit");

    const { propertyResaleDetail, loading } = useSelector((state) => ({
        propertyResaleDetail: state.propertiesResale?.propertyResaleDetail,
        loading: state.propertyResale?.loading,
    }));

    const data = propertyResaleDetail?.data;

    useEffect(() => {
        if (!id) return;
        dispatch(viewPropertyResale(id)).catch(() => {
            toast.error("Failed to load property details.");
        });
    }, [id, dispatch]);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return "—";
        return new Date(dateString).toLocaleDateString("en-IN", {
            year: "numeric", month: "long", day: "numeric",
        });
    }, []);

    const statusClass = (status) => {
        const map = {
            published: "vrp-status-badge--published",
            approved: "vrp-status-badge--approved",
            requested: "vrp-status-badge--requested",
            unpublished: "vrp-status-badge--unpublished",
            sold: "vrp-status-badge--sold",
            declined: "vrp-status-badge--declined",
        };
        return map[status?.toLowerCase()] || "vrp-status-badge--unpublished";
    };

    const handleStatusChange = async (e) => {
        const status = e.target.value;
        try {
            const res = await dispatch(statusPropertyResale({ id, status }));
            if (res?.statusCode === 200) {
                dispatch(viewPropertyResale(id));
            }
        } catch {
            toast.error("Failed to update status");
        }
    };

    const toggleAdminMarginPopup = useCallback(() => setAdminMarginPopup(prev => !prev), []);

    /* ── existingAdminMargin — same shape as ViewProperties ── */
    const existingAdminMargin = useMemo(() => {
        const obj = data?.admin_margin;
        if (obj && typeof obj === "object" && obj.type && obj.margin != null) {
            return {
                admin_margin_type: String(obj.type),
                admin_margin_value: String(obj.margin),
            };
        }
        return null;
    }, [data?.admin_margin]);

    /* ── Loading / Not found ── */
    if (loading) {
        return (
            <MainLayout>
                <div className="vrp-loader-wrap">
                    <div className="vrp-loader" />
                    <p className="vrp-loader-text">Loading property details…</p>
                </div>
            </MainLayout>
        );
    }

    if (!data) {
        return (
            <MainLayout>
                <div className="vrp-loader-wrap">
                    <p className="vrp-loader-text">Property not found.</p>
                </div>
            </MainLayout>
        );
    }

    /* convenience */
    const coverPhoto = data.cover_photos?.[0]?.url;
    const bedroomCount = data.no_of_bedroom ?? data.details?.summary?.bedrooms ?? 0;
    const bathCount = data.no_of_bathroom ?? data.details?.summary?.washroom ?? 0;
    const kitchenCount = data.no_of_kitchen ?? data.details?.summary?.kitchen ?? 0;

    const safetyFeatures = Array.isArray(data.safety_features) ? data.safety_features : [];
    const addOnRooms = Array.isArray(data.additional_rooms) ? data.additional_rooms : [];
    const plotAmenities = Array.isArray(data.amenities) ? data.amenities : [];
    const vibes = Array.isArray(data.property_vibes) ? data.property_vibes : [];
    const faqs = Array.isArray(data.faqs) ? data.faqs : [];
    const documents = Array.isArray(data.documents) ? data.documents : [];

    return (
        <MainLayout>
            <div className="vrp-wrapper">

                {/* ── Header ── */}
                <div className="vrp-header">
                    <div className="vrp-header-left">
                        <Link to="/resale_properties" className="vrp-back-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Back
                        </Link>
                        <div className="vrp-header-divider" />
                        <div>
                            <div className="vrp-breadcrumb">Properties / Resale / View</div>
                            <h1 className="vrp-title">Property Details</h1>
                        </div>
                    </div>
                </div>

                {/* ── Hero ── */}
                <div className="vrp-hero-card">
                    {coverPhoto
                        ? <img src={coverPhoto} alt="cover" className="vrp-hero-cover" />
                        : <div className="vrp-hero-cover-placeholder" />
                    }
                    <div className="vrp-hero-overlay">
                        <div className="vrp-hero-badges">
                            <span className={`vrp-status-badge ${statusClass(data.status)}`}>
                                {data.status || "Unknown"}
                            </span>
                            {data.project_type?.name && (
                                <span className="vrp-type-badge">{data.project_type.name}</span>
                            )}
                            {data.category?.name && (
                                <span className="vrp-type-badge">{data.category.name}</span>
                            )}
                            {data.type && (
                                <span className="vrp-type-badge" style={{ textTransform: "capitalize" }}>
                                    {data.type}
                                </span>
                            )}
                        </div>
                        <h2 className="vrp-hero-name">
                            {data.property_location
                                ? `${data.category?.name || "Property"} — ${data.property_location}`
                                : `Property #${data.property_id || id}`}
                        </h2>
                        <div className="vrp-hero-meta">
                            {data.street_address && (
                                <span className="vrp-hero-meta-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                    </svg>
                                    {data.street_address}
                                </span>
                            )}
                            {data.possession_status && (
                                <span className="vrp-hero-meta-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="2" y="3" width="20" height="14" rx="2" />
                                        <line x1="8" y1="21" x2="16" y2="21" />
                                        <line x1="12" y1="17" x2="12" y2="21" />
                                    </svg>
                                    {data.possession_status.replace(/_/g, " ")}
                                </span>
                            )}
                            {data.property_id && (
                                <span className="vrp-hero-meta-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                    </svg>
                                    ID: {data.property_id}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Stats Row ── */}
                <div className="vrp-stats-row">
                    <div className="vrp-stat-card">
                        <span className="vrp-stat-label">Starting Price</span>
                        <span className="vrp-stat-value">
                            {formatCurrency(data.starting_price, data.currency || "INR")}
                        </span>
                        <span className="vrp-stat-sub">Base price</span>
                    </div>
                    <div className="vrp-stat-card">
                        <span className="vrp-stat-label">Super Area</span>
                        <span className="vrp-stat-value">
                            {data.super_area ? `${data.super_area}` : "—"}
                        </span>
                        <span className="vrp-stat-sub">{data.super_area_measure_in || "sq.ft"}</span>
                    </div>
                    <div className="vrp-stat-card">
                        <span className="vrp-stat-label">Config</span>
                        <span className="vrp-stat-value">
                            {bedroomCount ? `${bedroomCount} BHK` : "—"}
                        </span>
                        <span className="vrp-stat-sub">{bathCount} Bath · {kitchenCount} Kitchen</span>
                    </div>
                    <div className="vrp-stat-card">
                        <span className="vrp-stat-label">Status</span>
                        <select
                            value={data.status || ""}
                            onChange={handleStatusChange}
                            style={{
                                marginTop: 6,
                                padding: "6px 10px",
                                borderRadius: 8,
                                border: "1px solid #e4e7ec",
                                fontSize: 13,
                                fontWeight: 600,
                                cursor: "pointer",
                                outline: "none",
                                background: "#f9fafb",
                                color: "#111827",
                                width: "100%",
                            }}
                        >
                            <option value="requested">Requested</option>
                            <option value="approved">Approved</option>
                            <option value="declined">Declined</option>
                            <option value="unpublished">Unpublished</option>
                            <option value="published">Published</option>
                            <option value="sold">Sold</option>
                        </select>
                        <span className="vrp-stat-sub">Change status</span>
                    </div>
                </div>

                {/* ── Two-column Grid ── */}
                <div className="vrp-grid-layout">

                    {/* ── Left Column ── */}
                    <div className="vrp-col">

                        {/* Basic Info */}
                        <CardSection badge="01" title="Basic Info">
                            <div className="vrp-info-grid">
                                <InfoItem label="Property ID" value={data.property_id} />
                                <InfoItem label="Added By" value={data.added_by} />
                                <InfoItem label="Category" value={data.category?.name} />
                                <InfoItem label="Type" value={data.type} />
                                <InfoItem label="Project Name" value={data.project?.project_name} />
                                <InfoItem label="Project ID" value={data.project?.project_id} />
                                <InfoItem label="Possession Status" value={data.possession_status?.replace(/_/g, " ")} />
                                <InfoItem label="Possession" value={data.possession_month && data.possession_year ? `${data.possession_month} ${data.possession_year}` : "—"} />
                                <InfoItem label="Created At" value={formatDate(data.created_at)} />
                                <InfoItem label="Updated At" value={formatDate(data.updated_at)} />
                            </div>
                        </CardSection>

                        {/* Location */}
                        <CardSection badge="02" title="Location">
                            <div className="vrp-info-grid">
                                <InfoItem label="Property Location" value={data.property_location} />
                                <InfoItem label="Zipcode" value={data.zipcode || "—"} />
                                <InfoItem label="Street Address" value={data.street_address} full />
                            </div>
                            {data.location?.coordinates?.length === 2 && (
                                <div className="vrp-map-embed-wrap">
                                    <iframe
                                        title="Property Location"
                                        width="100%"
                                        height="220"
                                        style={{ border: 0, borderRadius: "var(--vrp-radius-lg)", display: "block" }}
                                        loading="lazy"
                                        allowFullScreen
                                        referrerPolicy="no-referrer-when-downgrade"
                                        src={`https://www.google.com/maps?q=${data.location.coordinates[1]},${data.location.coordinates[0]}&z=15&output=embed`}
                                    />
                                </div>
                            )}
                        </CardSection>

                        {/* Area & Pricing */}
                        <CardSection badge="03" title="Area & Pricing">
                            <div className="vrp-info-grid">
                                <InfoItem label="Super Area" value={data.super_area ? `${data.super_area} ${data.super_area_measure_in || "sq.ft"}` : "—"} />
                                <InfoItem label="Carpet Area" value={data.carpet_area ? `${data.carpet_area} ${data.carpet_area_measure_in || "sq.ft"}` : "—"} />
                                <InfoItem label="Built-up Area" value={data.built_up_area ? `${data.built_up_area} sq.ft` : "—"} />
                                <InfoItem label="Price / sq.ft" value={data.price_per_sq_ft ? formatCurrency(data.price_per_sq_ft) : "—"} />
                                <InfoItem label="Starting Price" value={formatCurrency(data.starting_price)} />
                                <InfoItem label="Maintenance" value={data.maintainence_value ? formatCurrency(data.maintainence_value) : "—"} />
                                <InfoItem label="Token Amount" value={data.token_amount ? formatCurrency(data.token_amount) : "—"} />
                                <InfoItem label="Maint. Free Period" value={data.maintainence_free_period ? `${data.maintainence_free_period} months` : "—"} />
                            </div>
                        </CardSection>

                        {/* Rooms & Config */}
                        <CardSection badge="04" title="Rooms & Configuration">
                            <div className="vrp-info-grid">
                                <InfoItem label="Bedrooms" value={bedroomCount || "—"} />
                                <InfoItem label="Bathrooms" value={bathCount || "—"} />
                                <InfoItem label="Kitchens" value={kitchenCount || "—"} />
                                <InfoItem label="Open Parking" value={data.open_parking ?? "—"} />
                                <InfoItem label="Cover Parking" value={data.cover_parking ?? "—"} />
                                <InfoItem label="No. of Cabins" value={data.no_of_cabins || "—"} />
                                <InfoItem label="No. of Seatings" value={data.no_of_seatings || "—"} />
                                <InfoItem label="Ceiling Height" value={data.ceiling_height || "—"} />
                                <InfoItem label="No. of Pantry" value={data.no_of_pantry || "—"} />
                                <InfoItem label="Swimming Pool" value={<BoolTag value={data.is_swimming_pool} />} />
                                <InfoItem label="Maintenance" value={<BoolTag value={data.maintainence} />} />
                                <InfoItem label="Pre-lease" value={<BoolTag value={data.is_pre_lease} />} />
                            </div>

                            {data.details?.summary && (
                                <div style={{ marginTop: 14 }}>
                                    <span className="vrp-info-label" style={{ display: "block", marginBottom: 8 }}>
                                        Room Summary
                                    </span>
                                    <div className="vrp-amenity-list">
                                        {Object.entries(data.details.summary).map(([key, val]) => (
                                            <span key={key} className="vrp-amenity-tag">
                                                {key.replace(/([A-Z])/g, " $1").trim()}: <strong style={{ marginLeft: 4 }}>{val}</strong>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {data.details?.grouped?.bedrooms?.length > 0 && (
                                <div style={{ marginTop: 14 }}>
                                    <span className="vrp-info-label" style={{ display: "block", marginBottom: 8 }}>
                                        Bedroom Details
                                    </span>
                                    <div className="vrp-spec-list">
                                        {data.details.grouped.bedrooms.map((br, i) => (
                                            <div key={i} className="vrp-faq-item">
                                                <p className="vrp-faq-q">Bedroom {i + 1}</p>
                                                <div className="vrp-spec-tags" style={{ marginTop: 4 }}>
                                                    {br.attachedBathroom > 0 && (
                                                        <span className="vrp-spec-tag vrp-spec-tag--verified">✓ Attached Bathroom</span>
                                                    )}
                                                    {br.attachedBalcony > 0 && (
                                                        <span className="vrp-spec-tag vrp-spec-tag--verified">✓ Attached Balcony</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardSection>

                        {/* Additional Rooms */}
                        {addOnRooms.length > 0 && (
                            <CardSection badge="05" title="Additional Rooms">
                                <div className="vrp-locality-list">
                                    {addOnRooms.map((room, i) => (
                                        <span key={i} className="vrp-locality-tag" style={{ textTransform: "capitalize" }}>
                                            {room}
                                        </span>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {/* About Property */}
                        {data.about_property && (
                            <CardSection badge="06" title="About Property">
                                <p className="vrp-description">{data.about_property}</p>
                            </CardSection>
                        )}

                        {/* Safety Features */}
                        {safetyFeatures.length > 0 && (
                            <CardSection badge="07" title="Safety Features">
                                <div className="vrp-spec-list">
                                    {safetyFeatures.map((sf, i) => (
                                        <span key={i} className="vrp-spec-tag vrp-spec-tag--verified" style={{ display: "inline-flex" }}>
                                            🛡 {sf}
                                        </span>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {/* FAQs */}
                        {faqs.length > 0 && (
                            <CardSection badge="08" title={`FAQs (${faqs.length})`}>
                                <div className="vrp-faq-list">
                                    {faqs.map((faq, i) => (
                                        <div key={i} className="vrp-faq-item">
                                            <p className="vrp-faq-q">Q: {faq.question}</p>
                                            <p className="vrp-faq-a">A: {faq.answer}</p>
                                        </div>
                                    ))}
                                </div>
                            </CardSection>
                        )}
                    </div>

                    {/* ── Right Column ── */}
                    <div className="vrp-col">

                        {/* Cover Photo */}
                        {coverPhoto && (
                            <CardSection badge="10" title="Cover Photo">
                                <div className="vrp-cover-wrap">
                                    <img src={coverPhoto} alt="cover" className="vrp-cover-img" />
                                </div>
                            </CardSection>
                        )}

                        {/* All Images */}
                        {data.images?.length > 0 && (
                            <CardSection badge="11" title={`Property Images (${data.images.length})`}>
                                <div className="vrp-images-grid">
                                    {data.images.map((img, i) => (
                                        <img
                                            key={i}
                                            src={img.url || img}
                                            alt={`img-${i}`}
                                            className="vrp-thumb"
                                        />
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {/* Property Vibes */}
                        {vibes.length > 0 && (
                            <CardSection badge="12" title="Property Vibes">
                                <div className="vrp-amenity-list">
                                    {vibes.map((v, i) => (
                                        <span key={i} className="vrp-amenity-tag">
                                            {v.icon && (
                                                <img src={v.icon} alt={v.name} className="vrp-amenity-icon" />
                                            )}
                                            {v.name}
                                        </span>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {/* Plot Amenities */}
                        {plotAmenities.length > 0 && (
                            <CardSection badge="13" title={`Amenities (${plotAmenities.length})`}>
                                <div className="vrp-amenity-list">
                                    {plotAmenities.map((a, i) => (
                                        <span key={i} className="vrp-amenity-tag">
                                            {a.image && (
                                                <img src={a.image} alt={a.name} className="vrp-amenity-icon" />
                                            )}
                                            {a.name || a}
                                        </span>
                                    ))}
                                </div>
                            </CardSection>
                        )}

                        {/* Furnished Inventory */}
                        <CardSection badge="14" title="Furnished Inventory">
                            <div className="vrp-info-grid">
                                <InfoItem label="Furnished Qty" value={data.furnished_quantity} />
                                <InfoItem label="Semi-furn Qty" value={data.semi_furnished_quantity} />
                                <InfoItem label="Unfurnished Qty" value={data.unfurnished_quantity} />
                                <InfoItem label="Total Quantity" value={data.total_quantity} />
                                <InfoItem label="Storage Area" value={data.storage_area ? `${data.storage_area} sq.ft` : "—"} />
                                <InfoItem label="Plot Quantity" value={data.plot_quantity || "—"} />
                            </div>
                        </CardSection>

                        {/* Rental & Token */}
                        <CardSection badge="15" title="Rental & Token Info">
                            <div className="vrp-info-grid">
                                <InfoItem label="Rental Details" value={data.rental_details || "—"} />
                                <InfoItem label="Leased To" value={data.leased_to || "—"} />
                                <InfoItem label="Token Amount" value={formatCurrency(data.token_amount)} />
                                <InfoItem label="Paid Token Amount" value={<BoolTag value={data.is_paid_token_amount} />} />
                                <InfoItem label="3D URL" value={data.three_d_url || "—"} />
                                <InfoItem label="360 URL" value={data.three_sixty_url || "—"} />
                            </div>
                        </CardSection>

                        {/* Documents */}
                        {documents.length > 0 && (
                            <CardSection badge="16" title={`Documents (${documents.length})`}>
                                <div className="vrp-yt-list">
                                    {documents.map((doc, i) => (
                                        <a
                                            key={i}
                                            href={doc.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="vrp-brochure-btn"
                                            style={{ marginBottom: 4 }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                                <line x1="12" y1="18" x2="12" y2="12" />
                                                <line x1="9" y1="15" x2="15" y2="15" />
                                            </svg>
                                            {doc.name?.split("_").slice(2).join("_") || `Document ${i + 1}`}
                                        </a>
                                    ))}
                                </div>
                            </CardSection>
                        )}
                        {/* ── Admin Margin ── */}
                        {canAdd && canEdit && (
                            <CardSection badge="09" title="Admin Margin">
                                <div className="vrp-margin-card-header">
                                    <div className="vrp-margin-card-title-row">
                                        <span className="vrp-margin-card-icon">💰</span>
                                        <p className="vrp-margin-card-title">Admin Margin</p>
                                    </div>
                                    <button
                                        className="vrp-margin-edit-btn"
                                        onClick={toggleAdminMarginPopup}
                                    >
                                        {existingAdminMargin ? "✏️ Edit" : "+ Add"}
                                    </button>
                                </div>

                                {existingAdminMargin ? (
                                    <div className="vrp-margin-section">
                                        <div className="vrp-margin-data-item">
                                            <span className="vrp-margin-data-label">Type</span>
                                            <span className="vrp-margin-type-badge">
                                                {existingAdminMargin.admin_margin_type === "percentage" && "% Percentage"}
                                                {existingAdminMargin.admin_margin_type === "flat" && "₹ Flat"}
                                                {existingAdminMargin.admin_margin_type === "per_sq_ft" && "□ Per Sq.ft"}
                                                {!["percentage", "flat", "per_sq_ft"].includes(existingAdminMargin.admin_margin_type) && existingAdminMargin.admin_margin_type}
                                            </span>
                                        </div>
                                        <div className="vrp-margin-data-item">
                                            <span className="vrp-margin-data-label">Value</span>
                                            <span className="vrp-margin-data-value">
                                                {existingAdminMargin.admin_margin_type === "percentage"
                                                    ? `${existingAdminMargin.admin_margin_value}%`
                                                    : `₹ ${Number(existingAdminMargin.admin_margin_value).toLocaleString("en-IN")}`}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="vrp-margin-empty">
                                        <span>—</span>
                                        <span>No admin margin configured</span>
                                    </div>
                                )}
                            </CardSection>
                        )}

                    </div>
                </div>
            </div>

            {/* ── Admin Margin Popup ── */}
            <AdminMarginPopup
                isOpen={adminMarginPopup}
                onClose={() => {
                    dispatch(viewPropertyResale(id));
                    setAdminMarginPopup(false);
                }}
                propertyId={id}
                existingData={existingAdminMargin}
            />

        </MainLayout>
    );
};

export default ViewPropertyResale;